// Theme registry - single source of truth for all theme definitions
// This is a pure data module with no React or provider dependencies

export const THEMES = {
  primaryTheme: {
    name: 'Primary Theme',
    colors: {
      background: '#FFFFFF',
      text: '#1A1C20',
      primary: '#0560FD',
      accent: '#27ACBB',
      cardBg: '#FFFFFF',
      buttonBg: '#0560FD',
      buttonText: '#FFFFFF'
    },
    fonts: {
      body: "'Inter', system-ui, sans-serif",
      heading: "'Inter', system-ui, sans-serif"
    },
    scout: {
      appearance: 'primary',
      animation: 'standard'
    },
    audio: {
      available: ['lofi'],
      default: null
    },
    borderRadius: '8px'
  },
  scholarMode: {
    name: 'Scholar Mode',
    colors: {
      background: '#F7F2E3', // Ivory parchment
      text: '#1F2D3D', // Deeper slate ink for better contrast
      primary: '#2E4057', // Stronger deep blue
      accent: '#7D3C98', // Deepened purple for hierarchy
      cardBg: '#FFF8E7', // Slightly off-white
      buttonBg: '#2E4057',
      buttonText: '#F7F2E3',
    },
    fonts: {
      body: "'Merriweather', 'Georgia', serif",
      heading: "'Playfair Display', 'Times New Roman', serif",
    },
    scout: {
      appearance: 'scholar', // Glasses, scholarly
      animation: 'gentle',
    },
    audio: {
      available: ['classical'],
      default: null
    },
    borderRadius: '4px',
  },
  
  darkLab: {
    name: 'Dark Lab',
    colors: {
      background: '#121212', // Jet black
      text: '#E4E4E4', // Off-white for readability
      primary: '#9B59B6', // Neon purple
      accent: '#3498DB', // Electric blue
      cardBg: '#1E1E1E', // Dark grey
      buttonBg: '#9B59B6',
      buttonText: '#E4E4E4',
    },
    fonts: {
      body: "'Roboto Mono', 'Courier New', monospace",
      heading: "'Roboto Mono', 'Courier New', monospace",
    },
    scout: {
      appearance: 'tech', // Glitchy with neon trails
      animation: 'glitch',
    },
    audio: {
      available: ['lofi', 'keyboard'],
      default: null
    },
    borderRadius: '0',
  },
  
  zenFocus: {
    name: 'Zen Focus',
    colors: {
      background: '#EDF2F0', // Sage-tinted grey
      text: '#333333', // Dark grey for readability
      primary: '#7D9D9C', // Muted green
      accent: '#576F72', // Darker muted green
      cardBg: '#F7FAF9', // Faintest green-white
      buttonBg: '#7D9D9C',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'Open Sans', 'Helvetica', sans-serif",
      heading: "'Montserrat', 'Arial', sans-serif",
    },
    scout: {
      appearance: 'zen', // Calm, meditative
      animation: 'breathing',
    },
    audio: {
      available: ['rain', 'forest', 'candle'],
      default: null
    },
    borderRadius: '12px', // Very rounded
  },
  
  grindMode: {
    name: 'Grind Mode',
    colors: {
      background: '#FBF9F9', // Warm neutral
      text: '#1A1A1A', // Near-black for high contrast
      primary: '#E74C3C', // Red alerts
      accent: '#C0392B', // Darker red
      cardBg: '#F8F8F8', // Light grey
      buttonBg: '#E74C3C',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'Roboto', 'Helvetica', sans-serif",
      heading: "'Oswald', 'Impact', sans-serif",
    },
    scout: {
      appearance: 'energetic', // Running, active
      animation: 'fast',
    },
    audio: {
      available: ['motivation', 'timer'],
      default: null
    },
    borderRadius: '2px', // Sharp corners
  },
  
  retroVibes: {
    name: 'Retro Vibes',
    colors: {
      background: '#2D0051', // Deep purple
      text: '#FFFFFF', // White for readability
      primary: '#FF6EC7', // Vaporwave pink
      accent: '#00FFFF', // Cyan
      cardBg: '#3F0071', // Slightly lighter purple
      buttonBg: '#FF6EC7',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'Press Start 2P', 'Courier New', monospace",
      heading: "'VT323', 'Courier New', monospace",
    },
    scout: {
      appearance: 'retro', // Sunglasses, headband
      animation: 'pixelated',
    },
    audio: {
      available: ['chiptune', 'synthwave'],
      default: null
    },
    borderRadius: '0', // Sharp corners for pixel art feel
  },
  
  cafeMode: {
    name: 'Café Mode',
    colors: {
      background: '#F5EBE0', // Light mocha
      text: '#5D4037', // Coffee brown
      primary: '#8D6E63', // Warm brown
      accent: '#A1887F', // Lighter warm brown
      cardBg: '#FFFCF7', // Warm cream
      buttonBg: '#8D6E63',
      buttonText: '#FFF8F0', // Warm cream instead of stark white
    },
    fonts: {
      body: "'Nunito', 'Helvetica', sans-serif",
      heading: "'Pacifico', 'Georgia', serif",
    },
    scout: {
      appearance: 'cozy', // Coffee, headphones
      animation: 'relaxed',
    },
    audio: {
      available: ['cafe', 'jazz'],
      default: null
    },
    borderRadius: '8px', // Gently rounded
  },
  
  docuHounds: {
    name: 'DocuHounds',
    /* Dark brand theme â€“ deep neutral surfaces with blue/gold accents */
    colors: {
      background: '#0d0f13', // Very dark slate backdrop
      text: '#f2c586', // Gold as primary text per request
      primary: '#3a7fc4', // Muted system blue
      accent: '#f2c586', // Brand gold accent
      cardBg: '#161b22', // Elevated panel (GitHub-esque dark panel)
      buttonBg: '#208bed',
      buttonText: '#ffffff',
    },
    fonts: {
      body: "'Inter', system-ui, sans-serif",
      heading: "'Inter', system-ui, sans-serif",
    },
    scout: {
      appearance: 'brand',
      animation: 'standard',
    },
    audio: {
      available: ['lofi','classical'],
      default: null
    },
    borderRadius: '8px',
  },
  
  stemEngine: {
    name: 'STEM Engine',
    colors: {
      background: '#F7FBFC', // White-blue
      text: '#18364C', // Navy blue
      primary: '#2196F3', // Bright blue
      accent: '#03A9F4', // Lighter blue
      cardBg: '#F8FBFF', // Faintest blue-white
      buttonBg: '#2196F3',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'IBM Plex Sans', 'Arial', sans-serif",
      heading: "'IBM Plex Mono', 'Courier New', monospace",
    },
    scout: {
      appearance: 'scientist', // Lab coat, goggles
      animation: 'precise',
    },
    audio: {
      available: ['digital'],
      default: null
    },
    borderRadius: '4px',
  },

  midnightGold: {
    name: 'Midnight Gold',
    colors: {
      background: '#0D0B08', // Warm dark with amber undertone
      text: '#F5E6C8',
      primary: '#D4AF37',
      accent: '#E8C547',
      cardBg: '#16140F', // Warm dark card surface
      buttonBg: '#D4AF37',
      buttonText: '#0B0B0F',
    },
    fonts: {
      body: "'Inter', system-ui, sans-serif",
      heading: "'Playfair Display', 'Times New Roman', serif",
    },
    scout: {
      appearance: 'brand',
      animation: 'standard',
    },
    audio: {
      available: ['lofi', 'classical'],
      default: null
    },
    borderRadius: '10px',
  },

  velvetNight: {
    name: 'Velvet Night',
    colors: {
      background: '#140E1F',
      text: '#F3E8FF',
      primary: '#A855F7',
      accent: '#EC4899',
      cardBg: '#1E1530',
      buttonBg: '#A855F7',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'Inter', system-ui, sans-serif",
      heading: "'Montserrat', 'Arial', sans-serif",
    },
    scout: {
      appearance: 'brand',
      animation: 'gentle',
    },
    audio: {
      available: ['lofi', 'rain'],
      default: null
    },
    borderRadius: '12px',
  },

  steelCore: {
    name: 'Steel Core',
    colors: {
      background: '#0A0A0A',
      text: '#E5E5E5',
      primary: '#4B5563',
      accent: '#9CA3AF',
      cardBg: '#111111',
      buttonBg: '#4B5563',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'Roboto', 'Helvetica', sans-serif",
      heading: "'Oswald', 'Impact', sans-serif",
    },
    scout: {
      appearance: 'energetic',
      animation: 'fast',
    },
    audio: {
      available: ['motivation'],
      default: null
    },
    borderRadius: '2px',
  },

  neonCircuit: {
    name: 'Neon Circuit',
    colors: {
      background: '#05080F',
      text: '#D1FAE5',
      primary: '#00FFA3',
      accent: '#00C2FF',
      cardBg: '#0B1220',
      buttonBg: '#00FFA3',
      buttonText: '#05080F',
    },
    fonts: {
      body: "'IBM Plex Mono', 'Courier New', monospace",
      heading: "'IBM Plex Sans', 'Arial', sans-serif",
    },
    scout: {
      appearance: 'tech',
      animation: 'glitch',
    },
    audio: {
      available: ['digital', 'synthwave'],
      default: null
    },
    borderRadius: '6px',
  },

  crimsonFocus: {
    name: 'Crimson Focus',
    colors: {
      background: '#120A0A',
      text: '#FEE2E2',
      primary: '#DC2626',
      accent: '#F87171',
      cardBg: '#1F1111',
      buttonBg: '#DC2626',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'Inter', system-ui, sans-serif",
      heading: "'Oswald', 'Impact', sans-serif",
    },
    scout: {
      appearance: 'energetic',
      animation: 'fast',
    },
    audio: {
      available: ['motivation', 'timer'],
      default: null
    },
    borderRadius: '6px',
  },

  softBloom: {
    name: 'Soft Bloom',
    colors: {
      background: '#FFF7FB',
      text: '#3B2F2F',
      primary: '#F472B6',
      accent: '#A78BFA',
      cardBg: '#FFFAFC', // Barely pink
      buttonBg: '#F472B6',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'Inter', system-ui, sans-serif",
      heading: "'Playfair Display', 'Times New Roman', serif",
    },
    scout: {
      appearance: 'brand',
      animation: 'gentle',
    },
    audio: {
      available: ['rain', 'classical'],
      default: null
    },
    borderRadius: '12px',
  },

  skylineBlue: {
    name: 'Skyline Blue',
    colors: {
      background: '#F0F9FF',
      text: '#0F172A',
      primary: '#0EA5E9',
      accent: '#38BDF8',
      cardBg: '#F8FCFF', // Barely blue-white
      buttonBg: '#0EA5E9',
      buttonText: '#FFFFFF',
    },
    fonts: {
      body: "'Inter', system-ui, sans-serif",
      heading: "'Montserrat', 'Arial', sans-serif",
    },
    scout: {
      appearance: 'brand',
      animation: 'standard',
    },
    audio: {
      available: ['lofi'],
      default: null
    },
    borderRadius: '8px',
  }
};

// Immutable list of all valid theme IDs
export const THEME_IDS = Object.freeze(Object.keys(THEMES));

// â”€â”€ User-facing theme metadata â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Maps each theme to display-friendly info + preview tokens derived from
// the real THEMES registry.  Nothing is hardcoded â€” colors come straight
// from the theme definitions above.
export const THEME_META = Object.freeze(
  Object.fromEntries(
    Object.entries(THEMES).map(([id, t]) => {
      const c = t.colors;
      const isDark = ['darkLab', 'retroVibes', 'docuHounds', 'midnightGold', 'velvetNight', 'steelCore', 'neonCircuit', 'crimsonFocus'].includes(id);
      return [id, {
        id,
        displayName: t.name,
        isDark,
        description: {
          primaryTheme: 'Clean. Bright. Ready to go',
          scholarMode: 'Warm and academic',
          darkLab: 'Built for late nights',
          zenFocus: 'Quiet. Clean. Locked in',
          grindMode: 'No distractions. Just work',
          retroVibes: 'Loud. Different. Yours',
          cafeMode: 'Cozy like your favorite spot',
          docuHounds: 'Brand flagship',
          stemEngine: 'Crisp and precise',
          midnightGold: 'Luxury. Power. Presence',
          velvetNight: 'Late-night productivity energy',
          steelCore: 'Brutal. No fluff',
          neonCircuit: 'System-level intensity',
          crimsonFocus: 'Locked in. Aggressive',
          softBloom: 'Clean. Aesthetic. Sharp',
          skylineBlue: 'Fresh and modern',
        }[id] || '',
        // 6 representative swatches from the actual theme
        swatches: [c.background, c.cardBg, c.primary, c.accent, c.buttonBg, c.text],
        // Tokens for the mini app preview
        preview: {
          pageBg: c.background,
          sidebarBg: isDark ? (id === 'docuHounds' ? '#101419' : id === 'retroVibes' ? '#220044' : '#181818') : c.cardBg,
          headerBg: isDark ? (id === 'docuHounds' ? '#161b22' : c.cardBg) : c.primary,
          cardBg: c.cardBg,
          accentBg: c.buttonBg,
          accentText: c.buttonText,
          textPrimary: c.text,
          textSecondary: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)',
        },
      }];
    })
  )
);

// Curated display order (same as ThemeSwitcher)
export const THEME_DISPLAY_ORDER = Object.freeze([
  'primaryTheme', 'docuHounds', 'scholarMode', 'darkLab',
  'zenFocus', 'retroVibes', 'cafeMode', 'stemEngine', 'grindMode',
  'midnightGold', 'velvetNight', 'steelCore', 'neonCircuit', 'crimsonFocus',
  'softBloom', 'skylineBlue',
]);
